import {Component, Inject, OnDestroy} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';

import {Subscription} from 'rxjs';

import {ApiError, TradingService, PermissionModel} from '@spa/api';

import {FormComponent} from '../../../../shared/widgets';
import {FormInterface} from '../../../../shared/interfaces';
import {DialogActionsEnum, DialogComponent} from '../../../../shared/components';

import {EventsService} from '../../services';
import {AssignClientAccountRightsForm} from '../../forms';
import {AssignAccountRightsSelectableEntityInterface} from '../../interfaces';
import {AssignClientAccountRightsFormErrorStatesEnum, TradingAccountTypesEnum} from '../../enums';

import {
    AssignClientAccountRightsAccountOrUserSelector,
    AssignClientAccountRightsDialogConfig
} from '../../../../../assets/descriptors';

import {AssignClientAccountRightsMessages} from '../../../../../assets/messages';

@Component({
    selector: 'app-assign-client-account-rights',
    templateUrl: './assign-client-account-rights.component.html',
    styleUrls: ['./assign-client-account-rights.component.scss']
})
export class AssignClientAccountRightsComponent
    extends DialogComponent<AssignClientAccountRightsComponent> implements FormInterface, OnDestroy {

    private formSubscription: Subscription;

    public assignClientAccountRightsForm: AssignClientAccountRightsForm;

    constructor(
        private tradingService: TradingService,
        private eventsService: EventsService,
        @Inject(MAT_DIALOG_DATA) public data: {
            userOrAccountId: number,
            userRelationType: number,
            permissions: PermissionModel[],
            selectableEntities: AssignAccountRightsSelectableEntityInterface[],
            /**
             * Set to 'true' when the dialog is use in PanelAccountsEditComponent
             */
            initForAccountView: boolean,
            /**
             * Set when the dialog is use in PanelAccountsEditComponent
             */
            accountType: number
        },
        public dialogRef: MatDialogRef<AssignClientAccountRightsComponent>
    ) {
        super(dialogRef);

        this.config = AssignClientAccountRightsDialogConfig();

        this.assignClientAccountRightsForm = new AssignClientAccountRightsForm(this.tradingService, this.data.initForAccountView);

        this.initForm(!this.data.accountType ? TradingAccountTypesEnum.Trading : this.data.accountType);
    }

    ngOnDestroy(): void {
        if (this.formSubscription) {
            this.formSubscription.unsubscribe();
        }
    }

    getFormMessages(controlLabel?: string, controlMaxLength?: number): { [p: string]: string } {
        return FormComponent.getFormMessages(controlLabel, controlMaxLength);
    }

    initForm(accountType: number = TradingAccountTypesEnum.Trading): void {
        this.assignClientAccountRightsForm.initForm(accountType, this.data.selectableEntities, this.data.permissions);
    }

    validateForm(): boolean {
        return this.assignClientAccountRightsForm.validateForm(this.getFormMessages);
    }

    onFormSubmit(action?: number): void {
        if (action !== DialogActionsEnum.Confirm) {
            this.dialogRef.close({action, status: !this.config.process.failed});
        } else {
            if (this.validateForm()) {
                this.startLoading(() => {
                    this.formSubscription = this.assignClientAccountRightsForm.createRequest(
                        this.data.userOrAccountId,
                        this.data.userRelationType
                    ).subscribe(this.dialogSubscribeHandler.bind(
                        this,
                        () => {
                            const formValues = this.assignClientAccountRightsForm.form.value;

                            const filteredAccounts = this.data
                                .selectableEntities.filter((entity: AssignAccountRightsSelectableEntityInterface) => {
                                    return entity.id === formValues[AssignClientAccountRightsAccountOrUserSelector];
                                });

                            this.config.process.succeedMessage = AssignClientAccountRightsMessages
                                .AssignRightsSucceed.message.replace(
                                    '<<reference>>',
                                    (filteredAccounts.length > 0) ? filteredAccounts[0].reference : ''
                                );

                            this.eventsService.forceClientAccountsAccessRightsGet.next(true);
                        },
                        (errors: ApiError[]) => {
                            this.config.process.failedMessage = errors.map((error: ApiError) => {
                                switch (error.status) {
                                    case AssignClientAccountRightsFormErrorStatesEnum.AssignRightsFailed:
                                        const formValues = this.assignClientAccountRightsForm.form.value;

                                        const filteredAccounts = this.data.selectableEntities
                                            .filter((entity: AssignAccountRightsSelectableEntityInterface) => {
                                                return entity.id === formValues[AssignClientAccountRightsAccountOrUserSelector];
                                            });

                                        return AssignClientAccountRightsMessages.AssignRightsFailed.message.replace(
                                            '<<reference>>',
                                            (filteredAccounts.length > 0) ? filteredAccounts[0].reference : ''
                                        );
                                    case AssignClientAccountRightsFormErrorStatesEnum.CheckAlreadyAssignedRightsFailed:
                                        return AssignClientAccountRightsMessages.AssignRightsCheckForExistenceFailed.message;
                                    case AssignClientAccountRightsFormErrorStatesEnum.AccessRightsAlreadyAssigned:
                                        return AssignClientAccountRightsMessages.AssignRightsCheckForExistenceNotPassed.message;
                                    default:
                                        return AssignClientAccountRightsMessages.UnknownError.message;
                                }
                            }).join('</br>');
                        })
                    );
                });
            }
        }
    }
}
